import { useState, useRef, useEffect } from "react";
import { redrawStoredStrokes } from "../utils/canvasHelpers";

export default function useCanvas() {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState("draw");
  const strokesRef = useRef([]);
  const currentStrokeRef = useRef(null);
  const [hasDrawings, setHasDrawings] = useState(false); // Track if canvas has drawings

  const startDrawing = (e) => {
    if (e.button !== 0 && e.isPrimary !== undefined && !e.isPrimary) return;

    setIsDrawing(true);
    const rect = e.target.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    currentStrokeRef.current = {
      type: currentTool,
      points: [{ x, y }],
      pressure: e.pressure || e.touches?.[0]?.force || 0.5,
    };
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;

    const rect = e.target.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    currentStrokeRef.current.points.push({
      x,
      y,
      pressure: e.pressure || e.touches?.[0]?.force || 0.5,
    });

    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    const points = currentStrokeRef.current.points;

    // Need at least two points to draw a line
    if (points.length < 2) return;

    ctx.moveTo(points[points.length - 2].x, points[points.length - 2].y);
    ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);

    if (currentStrokeRef.current.type === "erase") {
      ctx.save();
      ctx.lineWidth = 50;
      ctx.globalCompositeOperation = "destination-out";
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (
      currentStrokeRef.current &&
      currentStrokeRef.current.points &&
      currentStrokeRef.current.points.length > 1
    ) {
      // Save completed stroke in the order drawn
      strokesRef.current.push({
        ...currentStrokeRef.current,
        points: [...currentStrokeRef.current.points],
      });

      // Update hasDrawings state
      setHasDrawings(true);
    }

    // Reset current stroke
    currentStrokeRef.current = null;
  };

  const handlePointerDown = (e) => {
    startDrawing(e);

    // For single taps/clicks that don't have movement, draw a small dot
    if (
      currentStrokeRef.current &&
      currentStrokeRef.current.points.length === 1 &&
      canvasRef.current
    ) {
      const ctx = canvasRef.current.getContext("2d");
      const point = currentStrokeRef.current.points[0];

      ctx.beginPath();
      ctx.arc(point.x, point.y, 2, 0, 2 * Math.PI);

      if (currentStrokeRef.current.type === "erase") {
        ctx.save();
        ctx.globalCompositeOperation = "destination-out";
        ctx.fill();
        ctx.restore();
      } else {
        ctx.fill();
      }

      // Update hasDrawings state for single point
      setHasDrawings(true);
    }
  };

  // Undo the last stroke
  const undoLastStroke = () => {
    if (strokesRef.current.length === 0) return;

    // Remove the last stroke
    strokesRef.current.pop();

    // Check if canvas is now empty
    if (strokesRef.current.length === 0) {
      setHasDrawings(false);
    }

    // Clear canvas and redraw remaining strokes
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Redraw all the remaining strokes
    redrawStoredStrokes(canvas, strokesRef.current);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokesRef.current = [];
    currentStrokeRef.current = null;
    setHasDrawings(false);
  };

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas dimensions to match window size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Initialize canvas context with proper settings
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "black";
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Restore the drawing
    if (strokesRef.current.length > 0) {
      redrawStoredStrokes(canvas, strokesRef.current);
    }
  };

  // Initialize canvas and add event listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Initial canvas setup
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Prevent scrolling while drawing
    const preventScroll = (e) => {
      if (isDrawing) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Set touch-action to none to prevent default touch behaviors
    canvas.style.touchAction = "none";
    canvas.style.webkitTouchCallout = "none";
    canvas.style.webkitUserSelect = "none";
    canvas.style.userSelect = "none";

    // Add touch event listeners with passive: false
    canvas.addEventListener("touchstart", preventScroll, { passive: false });
    canvas.addEventListener("touchmove", preventScroll, { passive: false });
    canvas.addEventListener("touchend", preventScroll, { passive: false });

    // Handle pointer up event globally
    const handlePointerUp = () => {
      if (isDrawing) {
        stopDrawing();
      }
    };

    window.addEventListener("mouseup", handlePointerUp);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("touchend", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      canvas.removeEventListener("touchstart", preventScroll);
      canvas.removeEventListener("touchmove", preventScroll);
      canvas.removeEventListener("touchend", preventScroll);
      window.removeEventListener("mouseup", handlePointerUp);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("touchend", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [isDrawing]);

  return {
    canvasRef,
    isDrawing,
    currentTool,
    setCurrentTool,
    handlePointerDown,
    draw,
    stopDrawing,
    clearCanvas,
    undoLastStroke, // New undo function
    strokesRef,
    currentStrokeRef,
    hasDrawings, // Expose whether canvas has drawings
  };
}
